import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import { useProfileData } from '../../hooks/useProfileData';
import MealCard from './MealCard';
import './DailyMealBuilder.css';

/** Cultural content (Oromo/Amharic cuisine, culture-specific labels) only when language is am/om or user indicated. */
function useShowCulturalContent(profile) {
  const { i18n } = useTranslation();
  const lang = (i18n.language || '').toLowerCase();
  const isAmOrOm = lang === 'am' || lang === 'om' || lang.startsWith('am') || lang.startsWith('om');
  const cultureIndicated = profile?.culture && ['ETHIOPIAN', 'OROMO', 'AMHARIC'].includes(String(profile.culture).toUpperCase());
  return isAmOrOm || cultureIndicated;
}

function DailyMealBuilder({ userId, profile: propProfile, userPreferences: propPreferences, symptoms, onMealPlanChange, pendingMealSelection, onPendingMealProcessed }) {
  // Use unified profile data hook - this provides aggregated profile data from ProfileContext
  const {
    profile: contextProfile,
    preferences: contextPreferences,
    healthProfile: contextHealthProfile,
    checkAllergy
  } = useProfileData();
  const showCulturalContent = useShowCulturalContent(contextProfile || propProfile);
  
  // Use profile from context if available, otherwise fall back to prop
  const profile = contextProfile || propProfile;
  const userPreferences = contextPreferences || propPreferences;
  // Load saved meals from localStorage on mount
  const loadSavedMeals = () => {
    if (!userId) return { breakfast: null, lunch: null, dinner: null, snack: null };
    
    try {
      const saved = localStorage.getItem(`dailyMeals_${userId}`);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Check if saved meals are from today
        const savedDate = parsed.date;
        const today = new Date().toISOString().split('T')[0];
        if (savedDate === today) {
          return parsed.meals || { breakfast: null, lunch: null, dinner: null, snack: null };
        }
      }
    } catch (error) {
      console.error('Error loading saved meals:', error);
    }
    return { breakfast: null, lunch: null, dinner: null, snack: null };
  };
  
  const [meals, setMeals] = useState(loadSavedMeals);
  
  // Save meals to localStorage whenever they change
  useEffect(() => {
    if (userId && Object.values(meals).some(m => m !== null)) {
      try {
        const toSave = {
          date: new Date().toISOString().split('T')[0],
          meals: meals
        };
        localStorage.setItem(`dailyMeals_${userId}`, JSON.stringify(toSave));
        console.log('💾 Saved meals to localStorage:', meals);
      } catch (error) {
        console.error('Error saving meals:', error);
      }
    }
  }, [meals, userId]);
  
  const [recommendations, setRecommendations] = useState({
    breakfast: [],
    lunch: [],
    dinner: [],
    snack: []
  });
  
  // Track which meals have been used for each meal type to ensure rotation
  const [usedMealIndices, setUsedMealIndices] = useState({
    breakfast: new Set(),
    lunch: new Set(),
    dinner: new Set(),
    snack: new Set()
  });
  
  const [loading, setLoading] = useState(false);
  const [userMood, setUserMood] = useState('');
  // Use health profile from context if available, otherwise keep local state for backward compatibility
  const [localHealthProfile, setLocalHealthProfile] = useState(null);
  const healthProfile = contextHealthProfile || localHealthProfile;
  
  // Load saved hydration from localStorage
  const loadSavedHydration = () => {
    if (!userId) return 0;
    try {
      const saved = localStorage.getItem(`hydration_${userId}`);
      if (saved) {
        const parsed = JSON.parse(saved);
        const savedDate = parsed.date;
        const today = new Date().toISOString().split('T')[0];
        if (savedDate === today) {
          return parsed.cups || 0;
        }
      }
    } catch (error) {
      console.error('Error loading saved hydration:', error);
    }
    return 0;
  };
  
  const [hydrationCups, setHydrationCups] = useState(loadSavedHydration);
  
  // Save hydration to localStorage
  useEffect(() => {
    if (userId) {
      try {
        const toSave = {
          date: new Date().toISOString().split('T')[0],
          cups: hydrationCups
        };
        localStorage.setItem(`hydration_${userId}`, JSON.stringify(toSave));
      } catch (error) {
        console.error('Error saving hydration:', error);
      }
    }
  }, [hydrationCups, userId]);
  
  // Update meal plan when meals or hydration changes
  useEffect(() => {
    // Use setTimeout to defer the callback to avoid updating parent during render
    const timeoutId = setTimeout(() => {
      if (Object.values(meals).some(m => m !== null) || hydrationCups > 0) {
        const mealPlanData = {
          days: [{
            date: new Date().toISOString().split('T')[0],
            breakfast: meals.breakfast,
            lunch: meals.lunch,
            dinner: meals.dinner,
            snack: meals.snack,
            hydrationActualCups: hydrationCups
          }]
        };
        onMealPlanChange?.(mealPlanData);
      }
    }, 0);
    
    return () => clearTimeout(timeoutId);
  }, [meals, hydrationCups, onMealPlanChange]);
  const [showRecommendations, setShowRecommendations] = useState({});
  const [recommendationSearch, setRecommendationSearch] = useState({});
  const [recommendationFilter, setRecommendationFilter] = useState({});
  
  // Calculate header height dynamically
  useEffect(() => {
    const calculateHeaderHeight = () => {
      const headerWrapper = document.querySelector('.navigation-header-wrapper');
      if (headerWrapper) {
        const height = headerWrapper.offsetHeight;
        setHeaderHeight(height + 20); // Add 20px for extra padding
      }
    };
    
    calculateHeaderHeight();
    window.addEventListener('resize', calculateHeaderHeight);
    // Also check after a delay to ensure DOM is ready
    const timeoutId = setTimeout(calculateHeaderHeight, 100);
    const timeoutId2 = setTimeout(calculateHeaderHeight, 500);
    
    return () => {
      window.removeEventListener('resize', calculateHeaderHeight);
      clearTimeout(timeoutId);
      clearTimeout(timeoutId2);
    };
  }, []);

  // Fetch health profile for recommendation explanations (fallback if context doesn't have it)
  useEffect(() => {
    // If we have health profile from context, don't fetch
    if (contextHealthProfile) {
      return;
    }
    
    const fetchHealthProfile = async () => {
      if (!userId || !profile) return;
      
      try {
        // Get user preferences (only if not available from context)
        if (!contextPreferences) {
          const prefs = await api.getUserPreferences(userId);
          
          // Calculate health profile (we'll create a simple version here)
          // In a real scenario, we'd call a backend endpoint, but for now we'll construct it
          if (prefs && (prefs.currentWeight || prefs.height || prefs.medicalConditions)) {
            // We'll show the explanation even with partial data
            setLocalHealthProfile({
              bmi: prefs.currentWeight && prefs.height ? 
                (prefs.currentWeight / Math.pow(prefs.height / 100, 2)).toFixed(1) : null,
              medicalConditions: prefs.medicalConditions,
              complications: prefs.complications,
              currentWeight: prefs.currentWeight,
              height: prefs.height,
              activityLevel: profile?.activityLevel
            });
          }
        }
      } catch (error) {
        console.log('Could not load health profile:', error);
        // Not critical, continue without it
      }
    };
    
    fetchHealthProfile();
  }, [userId, profile, userPreferences, contextHealthProfile, contextPreferences]);
  
  // Debug: Log recommendations when they change
  useEffect(() => {
    const totalRecommendations = Object.values(recommendations).reduce((sum, arr) => sum + (arr?.length || 0), 0);
    console.log('📊 Recommendations updated. Total:', totalRecommendations, 'Breakdown:', {
      breakfast: recommendations.breakfast?.length || 0,
      lunch: recommendations.lunch?.length || 0,
      dinner: recommendations.dinner?.length || 0,
      snack: recommendations.snack?.length || 0
    });
  }, [recommendations]);
  
  // Debug: Log meals when they change
  useEffect(() => {
    const mealCount = Object.values(meals).filter(m => m !== null).length;
    console.log('🍽️ Meals updated. Count:', mealCount, 'Meals:', meals);
  }, [meals]);
  
  // Debug: Log showRecommendations state changes
  useEffect(() => {
    console.log('🔍 showRecommendations state changed:', showRecommendations);
    Object.entries(showRecommendations).forEach(([type, isOpen]) => {
      if (isOpen) {
        console.log(`✅ Modal is OPEN for ${type}`);
      }
    });
  }, [showRecommendations]);
  
  const loadRecommendations = useCallback(async () => {
    if (!userId) return;
    
    try {
      setLoading(true);
      
      // Get recent symptoms
      let symptomsList = [];
      if (symptoms && symptoms.length > 0) {
        symptomsList = symptoms.map(s => typeof s === 'string' ? s : s.symptomName || s.name);
      }
      
      // Load recommendations for each meal type
      const mealTypes = ['breakfast', 'lunch', 'dinner', 'snack'];
      const recommendationsData = {};
      
      for (const mealType of mealTypes) {
        try {
          console.log(`🔄 Loading recommendations for ${mealType}...`, { userId, userMood, symptomsList });
          const response = await api.getMealRecommendations(
            userId, 
            mealType, 
            userMood, 
            [], // filters
            symptomsList
          );
          
          console.log(`📥 Raw API response for ${mealType}:`, response);
          
          // apiClient already extracts data, so response is the data directly
          const meals = Array.isArray(response) ? response : (response?.data || response || []);
          recommendationsData[mealType] = meals;
          
          console.log(`✅ Loaded ${meals.length} recommendations for ${mealType}`, meals.length > 0 ? meals.slice(0, 2).map(m => m?.name || 'unnamed') : 'none');
          
          if (meals.length === 0) {
            console.warn(`⚠️ No recommendations found for ${mealType}.`);
            console.warn(`   Response was:`, response);
            console.warn(`   Check backend logs for: userId=${userId}, mealType=${mealType}`);
          }
        } catch (error) {
          console.error(`❌ Error loading ${mealType} recommendations:`, error);
          console.error('   Error response:', error.response);
          console.error('   Error data:', error.response?.data);
          console.error('   Error message:', error.message);
          console.error('   Full error:', error);
          recommendationsData[mealType] = [];
        }
      }
      
      setRecommendations(recommendationsData);
      
      // Reset used indices when loading new recommendations
      setUsedMealIndices({
        breakfast: new Set(),
        lunch: new Set(),
        dinner: new Set(),
        snack: new Set()
      });
    } catch (error) {
      console.error('Error loading recommendations:', error);
    } finally {
      setLoading(false);
    }
  }, [userId, userMood, symptoms]);
  
  const [showMealInfoModal, setShowMealInfoModal] = useState(false);
  const [showAllergyWarningModal, setShowAllergyWarningModal] = useState(false);
  const [showMealConfirmationModal, setShowMealConfirmationModal] = useState(false);
  const [selectedMealInfo, setSelectedMealInfo] = useState(null);
  const [allergyWarningData, setAllergyWarningData] = useState(null);
  const [confirmationMealData, setConfirmationMealData] = useState(null);
  const [headerHeight, setHeaderHeight] = useState(120); // Default fallback
  const [showNutrientMealsModal, setShowNutrientMealsModal] = useState(false);
  const [selectedNutrient, setSelectedNutrient] = useState(null);
  const [nutrientRichMeals, setNutrientRichMeals] = useState([]);
  const [loadingNutrientMeals, setLoadingNutrientMeals] = useState(false);
  
  // Check if meal contains user's allergens
  // Use the unified checkAllergy function from useProfileData hook
  const checkMealForAllergens = useCallback((meal) => {
    const allergyCheck = checkAllergy(meal.name || '', meal.ingredients || []);
    return {
      hasAllergens: allergyCheck.hasAllergy,
      allergens: allergyCheck.allergens
    };
  }, [checkAllergy]);
  
  // Get alternative meals that are safe
  const getAlternativeMeals = useCallback((mealType, currentMeal) => {
    const allMeals = recommendations[mealType] || [];
    return allMeals.filter(meal => {
      // Exclude current meal
      if (meal.id === currentMeal.id || meal.name === currentMeal.name) {
        return false;
      }
      // Only include safe meals (no allergens)
      const allergenCheck = checkMealForAllergens(meal);
      return !allergenCheck.hasAllergens;
    }).slice(0, 3); // Show top 3 alternatives
  }, [recommendations, checkMealForAllergens]);
  
  const handleMealSelect = useCallback((mealType, meal) => {
    console.log('✅ handleMealSelect called:', { mealType, meal: meal?.name || meal, mealData: meal });
    console.log('🔍 Meal has whyRecommended?', meal?.whyRecommended ? 'YES ✅' : 'NO ❌');
    
    // Allow null meal for removal, but mealType is required
    if (!mealType) {
      console.error('❌ Invalid mealType:', { meal, mealType });
      return;
    }
    
    // If meal is null, this is a removal operation
    if (meal === null) {
      console.log('🗑️ Removing meal for:', mealType);
      setMeals(prev => {
        const updatedMeals = {
          ...prev,
          [mealType]: null
        };
        
        // Notify parent of meal plan change
        const mealPlanData = {
          days: [{
            date: new Date().toISOString().split('T')[0],
            breakfast: updatedMeals.breakfast,
            lunch: updatedMeals.lunch,
            dinner: updatedMeals.dinner,
            snack: updatedMeals.snack,
            hydrationActualCups: hydrationCups
          }]
        };
        
        setTimeout(() => {
          if (onMealPlanChange) {
            onMealPlanChange(mealPlanData);
          }
        }, 0);
        
        return updatedMeals;
      });
      return;
    }
    
      // Check for allergens first
      const allergenCheck = checkMealForAllergens(meal);
      
      if (allergenCheck.hasAllergens) {
        // Show allergy warning modal instead
        const alternatives = getAlternativeMeals(mealType, meal);
        setAllergyWarningData({
          meal,
          mealType,
          allergens: allergenCheck.allergens,
          alternatives
        });
        setShowAllergyWarningModal(true);
        return; // Don't add the meal yet
    }
    
    // Show confirmation modal before adding
    setConfirmationMealData({ meal, mealType });
    setShowMealConfirmationModal(true);
  }, [checkMealForAllergens, getAlternativeMeals, hydrationCups, onMealPlanChange]);
  
  useEffect(() => {
    if (userId) {
      console.log('🔄 Loading recommendations for:', { userId, hasProfile: !!profile, userMood, symptomsCount: symptoms?.length || 0 });
      // Backend will handle missing profile by defaulting to CONCEIVING state
      loadRecommendations();
    } else {
      console.warn('⚠️ Cannot load recommendations - missing userId:', { userId });
    }
  }, [userId, profile, symptoms, userMood, loadRecommendations]); // Reload when mood or symptoms change
  
  // Handle pending meal selection from explorer
  useEffect(() => {
    if (pendingMealSelection && pendingMealSelection.mealType && pendingMealSelection.meal) {
      console.log('✅ Processing pending meal selection:', pendingMealSelection);
      handleMealSelect(pendingMealSelection.mealType, pendingMealSelection.meal);
      if (onPendingMealProcessed) {
        onPendingMealProcessed();
      }
    }
  }, [pendingMealSelection, handleMealSelect, onPendingMealProcessed]);

  const confirmAddMeal = () => {
    if (!confirmationMealData) return;
    
    const { meal, mealType } = confirmationMealData;
    
    // Close confirmation modal
    setShowMealConfirmationModal(false);
    
    // Normalize omega-3 value before adding meal
    const normalizedMeal = { ...meal };
    if (normalizedMeal.omega3 || normalizedMeal['omega-3']) {
      let omega3Value = normalizedMeal.omega3 || normalizedMeal['omega-3'] || 0;
      // Convert grams to milligrams if needed
      if (omega3Value > 0 && omega3Value < 1) {
        omega3Value = omega3Value * 1000;
      }
      // Ensure omega3 is set (not omega-3) for consistency
      normalizedMeal.omega3 = omega3Value;
      delete normalizedMeal['omega-3'];
    }
    
    // Add the meal
    setMeals(prev => {
      const updatedMeals = {
        ...prev,
        [mealType]: normalizedMeal
      };
      
      console.log('✅ Updated meals state:', updatedMeals);
      
      // Notify parent of meal plan change with updated meals
      const mealPlanData = {
        days: [{
          date: new Date().toISOString().split('T')[0],
          breakfast: updatedMeals.breakfast,
          lunch: updatedMeals.lunch,
          dinner: updatedMeals.dinner,
          snack: updatedMeals.snack,
          hydrationActualCups: hydrationCups
        }]
      };
      
      console.log('✅ Notifying parent with meal plan data:', mealPlanData);
      // Defer the callback to avoid updating parent during render
      setTimeout(() => {
        if (onMealPlanChange) {
          onMealPlanChange(mealPlanData);
        } else {
          console.warn('⚠️ onMealPlanChange callback not provided');
        }
      }, 0);
      
      return updatedMeals;
    });
    
    // Close recommendations panel
      setShowRecommendations(prev => ({ ...prev, [mealType]: false }));
    
    // Show information modal after adding
    setSelectedMealInfo({ meal, mealType });
    setShowMealInfoModal(true);
    
    // Clear confirmation data
    setConfirmationMealData(null);
  };
  
  const cancelAddMeal = () => {
    setShowMealConfirmationModal(false);
    setConfirmationMealData(null);
  };
  
  const handleRemove = (mealType) => {
    console.log('🗑️ Removing meal for:', mealType);
    handleMealSelect(mealType, null);
  };
  
  const handleSwap = async (mealType) => {
    try {
      console.log('🔄 Swapping meal for:', mealType);
      const currentMeal = meals[mealType];
      
      if (!currentMeal) {
        console.warn('⚠️ No meal to swap for:', mealType);
        // If no meal, just open recommendations instead
        setShowRecommendations(prev => ({ ...prev, [mealType]: true }));
        return;
      }
      
      // Get alternative recommendations
      let alternatives = recommendations[mealType] || [];
      
      // If no recommendations loaded, fetch them
      if (alternatives.length === 0) {
        console.log('📥 No recommendations cached, loading...');
        setLoading(true);
        try {
          // Get recent symptoms
          let symptomsList = [];
          if (symptoms && symptoms.length > 0) {
            symptomsList = symptoms.map(s => typeof s === 'string' ? s : s.symptomName || s.name);
          }
          
          const response = await api.getMealRecommendations(
            userId, 
            mealType, 
            userMood, 
            [], // filters
            symptomsList
          );
          
          const meals = Array.isArray(response) ? response : (response?.data || response || []);
          alternatives = meals;
          setRecommendations(prev => ({ ...prev, [mealType]: meals }));
          console.log(`✅ Loaded ${meals.length} recommendations for swap`);
          
          // Reset used indices when loading new recommendations
          setUsedMealIndices(prev => ({
            ...prev,
            [mealType]: new Set()
          }));
        } catch (error) {
          console.error(`❌ Error loading recommendations for swap:`, error);
        } finally {
          setLoading(false);
        }
      }
      
      if (alternatives.length === 0) {
        console.warn('⚠️ No alternatives available for:', mealType);
        // Open recommendations panel to let user select
        setShowRecommendations(prev => ({ ...prev, [mealType]: true }));
        return;
      }
      
      // Find current meal index in alternatives
      const currentMealIndex = alternatives.findIndex(m => {
        if (m.id && currentMeal.id) return m.id === currentMeal.id;
        if (m.name && currentMeal.name) return m.name === currentMeal.name;
        return false;
      });
      
      // Get used indices for this meal type
      const usedIndices = usedMealIndices[mealType] || new Set();
      
      // If current meal is found, mark it as used
      if (currentMealIndex !== -1) {
        setUsedMealIndices(prev => {
          const newSet = new Set(prev[mealType]);
          newSet.add(currentMealIndex);
          return { ...prev, [mealType]: newSet };
        });
      }
      
      // Find next available meal that hasn't been used
      let nextIndex = -1;
      let attempts = 0;
      const maxAttempts = alternatives.length;
      
      // Try to find an unused alternative
      while (attempts < maxAttempts) {
        // Start from a random position to ensure variety
        const startIndex = Math.floor(Math.random() * alternatives.length);
        
        for (let i = 0; i < alternatives.length; i++) {
          const checkIndex = (startIndex + i) % alternatives.length;
          
          // Skip current meal
          if (checkIndex === currentMealIndex) continue;
          
          // Skip if already used
          if (usedIndices.has(checkIndex)) continue;
          
          // Check if it's different from current meal
          const candidate = alternatives[checkIndex];
          const isDifferent = 
            (candidate.id && currentMeal.id && candidate.id !== currentMeal.id) ||
            (candidate.name && currentMeal.name && candidate.name !== currentMeal.name) ||
            (!candidate.id && !currentMeal.id && !candidate.name && !currentMeal.name);
          
          if (isDifferent) {
            nextIndex = checkIndex;
            break;
          }
        }
        
        if (nextIndex !== -1) break;
        
        // If all meals have been used, reset and start fresh
        if (usedIndices.size >= alternatives.length - 1) {
          console.log(`🔄 All meals used for ${mealType}, resetting rotation`);
          setUsedMealIndices(prev => ({
            ...prev,
            [mealType]: new Set()
          }));
          // Try again with reset indices
          attempts++;
          continue;
        }
        
        // If we can't find an unused one, pick any different one
        for (let i = 0; i < alternatives.length; i++) {
          const checkIndex = (startIndex + i) % alternatives.length;
          if (checkIndex === currentMealIndex) continue;
          
          const candidate = alternatives[checkIndex];
          const isDifferent = 
            (candidate.id && currentMeal.id && candidate.id !== currentMeal.id) ||
            (candidate.name && currentMeal.name && candidate.name !== currentMeal.name);
          
          if (isDifferent) {
            nextIndex = checkIndex;
            break;
          }
        }
        
        if (nextIndex !== -1) break;
        attempts++;
      }
      
      // If we found a next meal, use it
      if (nextIndex !== -1) {
        const alternative = alternatives[nextIndex];
        console.log(`✅ Swapping to meal ${nextIndex + 1}/${alternatives.length}:`, alternative.name || alternative.id);
        
        // Mark this index as used
        setUsedMealIndices(prev => {
          const newSet = new Set(prev[mealType]);
          newSet.add(nextIndex);
          return { ...prev, [mealType]: newSet };
        });
        
        handleMealSelect(mealType, alternative);
      } else {
        // Fallback: just pick a random different one
        const differentAlternatives = alternatives.filter((m, idx) => {
          if (idx === currentMealIndex) return false;
          return (m.id && currentMeal.id && m.id !== currentMeal.id) ||
                 (m.name && currentMeal.name && m.name !== currentMeal.name);
        });
        
        if (differentAlternatives.length > 0) {
          const randomAlternative = differentAlternatives[Math.floor(Math.random() * differentAlternatives.length)];
          console.log('✅ Swapping to random alternative:', randomAlternative.name || randomAlternative.id);
          handleMealSelect(mealType, randomAlternative);
        } else {
          console.warn('⚠️ No alternative found for:', mealType);
          // Open recommendations panel
          setShowRecommendations(prev => ({ ...prev, [mealType]: true }));
        }
      }
    } catch (error) {
      console.error('❌ Error swapping meal:', error);
    }
  };
  
  const getNutrientGoals = () => {
    if (!profile?.lmpDate) return null;
    
    // Calculate week and trimester
    const lmpDate = new Date(profile.lmpDate);
    const today = new Date();
    const daysDiff = Math.floor((today - lmpDate) / (1000 * 60 * 60 * 24));
    const week = Math.floor(daysDiff / 7);
    const trimester = week < 13 ? 1 : week < 27 ? 2 : 3;
    
    return {
      iron: 27, // mg
      protein: trimester === 1 ? 60 : trimester === 2 ? 70 : 75, // grams
      folate: 600, // mcg
      calcium: 1000, // mg
      fiber: trimester === 3 ? 28 : 25, // grams
      omega3: 200 // mg
    };
  };
  
  const calculateCurrentIntake = () => {
    const intake = {
      iron: 0,
      protein: 0,
      folate: 0,
      calcium: 0,
      fiber: 0,
      omega3: 0
    };
    
    Object.values(meals).forEach(meal => {
      if (meal) {
        if (meal.iron) intake.iron += meal.iron;
        if (meal.protein) intake.protein += meal.protein;
        if (meal.folate) intake.folate += meal.folate;
        if (meal.calcium) intake.calcium += meal.calcium;
        if (meal.fiber) intake.fiber += meal.fiber;
        
        // Handle omega-3 with unit conversion
        // Check both omega3 and omega-3 field names
        let omega3Value = meal.omega3 || meal['omega-3'] || 0;
        
        if (omega3Value > 0) {
          // If value is less than 1, it's likely in grams (e.g., 0.2g = 200mg)
          // If value is 1 or greater, assume it's already in mg
          // This handles both cases: 0.2g (should be 200mg) and 200mg (already correct)
          if (omega3Value < 1) {
            // Convert grams to milligrams
            omega3Value = omega3Value * 1000;
          }
          intake.omega3 += omega3Value;
        }
      }
    });
    
    return intake;
  };
  
  const nutrientGoals = getNutrientGoals();
  
  // Load nutrient-rich meals for a specific nutrient
  const loadNutrientRichMeals = async (nutrient) => {
    if (!userId) return;
    
    setLoadingNutrientMeals(true);
    setSelectedNutrient(nutrient);
    setShowNutrientMealsModal(true);
    
    try {
      // Get meals from all meal types and filter by nutrient content
      const mealTypes = ['breakfast', 'lunch', 'dinner', 'snack'];
      const allMeals = [];
      
      for (const mealType of mealTypes) {
        try {
          const response = await api.getMealRecommendations(
            userId,
            mealType,
            userMood,
            [],
            symptoms?.map(s => typeof s === 'string' ? s : s.symptomName || s.name) || []
          );
          
          const meals = Array.isArray(response) ? response : (response?.data || response || []);
          // Add mealType to each meal for filtering
          meals.forEach(meal => {
            meal.mealType = mealType;
          });
          allMeals.push(...meals);
        } catch (error) {
          console.error(`Error loading ${mealType} recommendations:`, error);
        }
      }
      
      // Filter meals that are rich in the selected nutrient
      // Normalize nutrient key (handle omega-3 -> omega3 conversion)
      let normalizedNutrientKey = nutrient.toLowerCase().replace(/-/g, '');
      // Handle special case: omega-3 -> omega3
      if (normalizedNutrientKey === 'omega3' || normalizedNutrientKey === 'omega-3') {
        normalizedNutrientKey = 'omega3';
      }
      
      const filteredMeals = allMeals
        .filter(meal => {
          // Create a comprehensive text search string for fallback (same as regular filter)
          const mealText = [
            meal.name || '',
            meal.description || '',
            ...(meal.ingredients || []),
            ...(meal.nutrients || []),
            meal.tags?.join(' ') || ''
          ].join(' ').toLowerCase();
          
          // Use the SAME filtering logic as the regular filter option (lines 1217-1223)
          if (normalizedNutrientKey === 'omega3') {
            // Check nutrients array or tags
            if (meal.nutrients?.some(n => n.toLowerCase().includes('omega'))) return true;
            if (meal.omega3 || meal.tags?.some(t => t.toLowerCase().includes('omega'))) return true;
            // Check ingredients for omega-3 rich foods (same list as regular filter)
            const omegaFoods = ['salmon', 'tuna', 'sardines', 'walnut', 'walnuts', 'chia', 'flax', 'flaxseed', 'cod', 'mackerel'];
            return omegaFoods.some(food => mealText.includes(food));
          }
          
          // For other nutrients, use the numeric value approach
          const nutrientValue = meal[normalizedNutrientKey] || meal[nutrient.toLowerCase()] || meal[nutrient.toLowerCase().replace(/-/g, '')];
          
          if (!nutrientValue || nutrientValue === 0) return false;
          
          // Get goal for this nutrient (use normalized key)
          const goal = nutrientGoals?.[normalizedNutrientKey] || nutrientGoals?.[nutrient.toLowerCase()] || 0;
          if (goal === 0) return false;
          
          // Consider meals that provide at least 10% of daily goal
          const percentageOfGoal = (nutrientValue / goal) * 100;
          return percentageOfGoal >= 10;
        })
        .sort((a, b) => {
          // For omega-3, we can't reliably sort by numeric value since many meals don't have it
          // So we'll keep them in the order they were received (from API)
          if (normalizedNutrientKey === 'omega3') {
            return 0; // Keep original order
          }
          
          // For other nutrients, sort by nutrient content (highest first)
          const aValue = a[normalizedNutrientKey] || a[nutrient.toLowerCase()] || a[nutrient.toLowerCase().replace(/-/g, '')] || 0;
          const bValue = b[normalizedNutrientKey] || b[nutrient.toLowerCase()] || b[nutrient.toLowerCase().replace(/-/g, '')] || 0;
          return bValue - aValue;
        })
        .slice(0, 12); // Show top 12 nutrient-rich meals
      
      setNutrientRichMeals(filteredMeals);
    } catch (error) {
      console.error('Error loading nutrient-rich meals:', error);
      setNutrientRichMeals([]);
    } finally {
      setLoadingNutrientMeals(false);
    }
  };
  
  return (
    <div className="daily-meal-builder">
      {/* User Mood Selector */}
      <div className="mood-selector">
        <label className="mood-label">How are you feeling today?</label>
        <div className="mood-options">
          {['light', 'comfort', 'energizing', 'gentle'].map((mood) => (
            <motion.button
              key={mood}
              className={`mood-option ${userMood === mood ? 'active' : ''}`}
              onClick={() => setUserMood(mood)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {mood === 'light' && '🥗'}
              {mood === 'comfort' && '🍲'}
              {mood === 'energizing' && '⚡'}
              {mood === 'gentle' && '💚'}
              <span>{mood.charAt(0).toUpperCase() + mood.slice(1)}</span>
            </motion.button>
          ))}
        </div>
      </div>
      
      {/* Meal Boards */}
      <div className="meal-boards-grid">
        {['breakfast', 'lunch', 'dinner', 'snack'].map((mealType) => (
          <motion.div
            key={mealType}
            className={`meal-board ${!meals[mealType] ? 'empty' : ''}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="meal-board-header">
              <h3 className="meal-board-title">
                {mealType === 'breakfast' && '🌅'}
                {mealType === 'lunch' && '☀️'}
                {mealType === 'dinner' && '🌙'}
                {mealType === 'snack' && '🍎'}
                {' '}
                {mealType.charAt(0).toUpperCase() + mealType.slice(1)}
              </h3>
              {meals[mealType] && (
                <motion.button
                  className="swap-button"
                  onClick={() => handleSwap(mealType)}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  title="Swap this meal"
                >
                  🔄 Swap
                </motion.button>
              )}
            </div>
            
            <div className="meal-board-content">
              {meals[mealType] ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <MealCard
                    meal={meals[mealType]}
                    onRemove={() => handleRemove(mealType)}
                    onSwap={() => handleSwap(mealType)}
                    expandable={true}
                  />
                </motion.div>
              ) : (
                <div className="empty-meal-slot">
                  <p className="empty-message">
                    {mealType === 'breakfast' && "You didn't add breakfast yet. Want a gentle idea?"}
                    {mealType !== 'breakfast' && `No ${mealType} added yet. Tap to add one!`}
                  </p>
                  <motion.button
                    className="add-meal-button"
                    onClick={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log('➕ Button clicked - Opening recommendations for:', mealType);
                      console.log('Current showRecommendations state:', showRecommendations);
                      
                      // If no recommendations loaded, load them first
                      if (!recommendations[mealType] || recommendations[mealType].length === 0) {
                        console.log('📥 Loading recommendations for:', mealType);
                        setLoading(true);
                        try {
                          let symptomsList = [];
                          if (symptoms && symptoms.length > 0) {
                            symptomsList = symptoms.map(s => typeof s === 'string' ? s : s.symptomName || s.name);
                          }
                          
                          const response = await api.getMealRecommendations(
                            userId, 
                            mealType, 
                            userMood, 
                            [], // filters
                            symptomsList
                          );
                          
                          const meals = Array.isArray(response) ? response : (response?.data || response || []);
                          setRecommendations(prev => ({ ...prev, [mealType]: meals }));
                          console.log(`✅ Loaded ${meals.length} recommendations`);
                        } catch (error) {
                          console.error(`❌ Error loading recommendations:`, error);
                        } finally {
                          setLoading(false);
                        }
                      }
                      
                      // Set showRecommendations to true for this meal type
                      setShowRecommendations(prev => {
                        const newState = { ...prev, [mealType]: true };
                        console.log('🔄 Setting showRecommendations to:', newState);
                        return newState;
                      });
                    }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    disabled={loading}
                    style={{ pointerEvents: 'auto', zIndex: 10 }}
                  >
                    {loading ? '⏳ Loading...' : `+ Add ${mealType.charAt(0).toUpperCase() + mealType.slice(1)}`}
                  </motion.button>
                </div>
              )}
            </div>
            
            {/* Full-Screen Meal Selection Modal */}
            {createPortal(
              <AnimatePresence mode="wait">
              {showRecommendations[mealType] && (
                <motion.div
                    key={`meal-modal-${mealType}`}
                    className="meal-selection-modal-overlay"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    onClick={(e) => {
                      if (e.target === e.currentTarget) {
                        setShowRecommendations(prev => ({ ...prev, [mealType]: false }));
                      }
                    }}
                  >
                    <motion.div
                      className="meal-selection-modal"
                      initial={{ scale: 0.95, opacity: 0, y: 20 }}
                      animate={{ scale: 1, opacity: 1, y: 0 }}
                      exit={{ scale: 0.95, opacity: 0, y: 20 }}
                      transition={{ duration: 0.3 }}
                      onClick={(e) => e.stopPropagation()}
                    >
                    {/* Modal Header - Spacious and Clear */}
                    <div className="meal-selection-header">
                      <div className="meal-selection-header-content">
                        <div className="meal-selection-title-section">
                          <h2 className="meal-selection-title">
                            {mealType === 'breakfast' && '🌅'}
                            {mealType === 'lunch' && '☀️'}
                            {mealType === 'dinner' && '🌙'}
                            {mealType === 'snack' && '🍎'}
                            {' '}
                            Choose Your {mealType.charAt(0).toUpperCase() + mealType.slice(1)}
                          </h2>
                          <p className="meal-selection-subtitle">
                            Browse through our curated selection of pregnancy-friendly meals
                          </p>
                          {/* Based on your profile – so user knows exactly what we used */}
                          {(profile || userPreferences) && (
                            <div className="meal-selection-based-on">
                              <strong className="meal-selection-based-on-label">Recommendations based on: </strong>
                              <span className="meal-selection-based-on-list">
                                {(() => {
                                  let week = profile?.currentWeek;
                                  if (!week && profile?.lmpDate) {
                                    const lmp = new Date(profile.lmpDate);
                                    const days = Math.floor((new Date() - lmp) / (1000 * 60 * 60 * 24));
                                    week = Math.floor(days / 7) + 1;
                                  }
                                  return [
                                  week && `Pregnancy week ${week}`,
                                  userPreferences?.allergies?.length > 0 && `Your allergies (allergen-containing meals still shown with ⚠️)`,
                                  userPreferences?.cookingTime && `${userPreferences.cookingTime === 'QUICK' ? 'Quick' : userPreferences.cookingTime === 'MODERATE' ? 'Moderate' : 'Flexible'} prep`,
                                  userPreferences?.cookingSkill && `${userPreferences.cookingSkill} cooking`,
                                  userPreferences?.morningSicknessSeverity && userPreferences.morningSicknessSeverity !== 'NONE' && 'Nausea-friendly',
                                  userPreferences?.foodPreferences?.length > 0 && `Taste: ${userPreferences.foodPreferences.join(', ')}`,
                                  userPreferences?.favoriteFoods && 'Your favorite foods',
                                  userPreferences?.dislikedFoods && 'Your disliked foods (still shown with a note)',
                                  userPreferences?.medicalConditions && 'Your medical considerations',
                                  showCulturalContent && profile?.culture && `Your cuisine (${profile.culture})`,
                                  userPreferences?.dailyCalorieGoal && `~${userPreferences.dailyCalorieGoal} kcal goal`,
                                  symptoms?.length > 0 && `How you're feeling (${symptoms.slice(0, 2).map(s => typeof s === 'string' ? s : s.symptomName || s.name).join(', ')})`
                                ].filter(Boolean).join(' • ');
                                })()}
                              </span>
                            </div>
                          )}
                          {/* How we personalize – full transparency */}
                          <details className="meal-selection-how-we-personalize">
                            <summary className="meal-selection-how-summary">How we choose these meals</summary>
                            <div className="meal-selection-how-content">
                              <p><strong>Why some meals are at the top:</strong> We match your cooking time, skill, diet, taste, and stage (pregnancy week / TTC / postpartum). Meals that fit best get higher priority.</p>
                              <p><strong>Allergies:</strong> Meals that contain ingredients you listed as allergens are still shown so you can see everything—but they’re clearly marked (⚠️). You can add them with &quot;Add anyway (I&apos;ll be cautious)&quot; if you choose.</p>
                              <p><strong>Things to note:</strong> If a meal doesn’t match your usual prep time or contains something you dislike, we still show it and explain why (e.g. &quot;Takes 45 min; you prefer ≤30 min&quot;) so you can decide.</p>
                              <p><strong>Every detail is used:</strong> Your medical conditions, morning sickness, energy level, favorite and disliked foods{showCulturalContent && profile?.culture ? ', and cuisine preference' : ''} all shape which meals we suggest and how we describe them.</p>
                              <p><strong>Where you see it:</strong> On each meal card look for the green <strong>Why we recommend this</strong>, the 📌 <strong>Things to note</strong> (or &quot;Why we still show it&quot;) when a meal doesn&apos;t fully match, and the ⚠️ warning on allergen-containing meals—you can still add those with &quot;Add anyway (I&apos;ll be cautious)&quot;. To change what we use, update your preferences in Profile.</p>
                            </div>
                          </details>
                        </div>
                        <motion.button
                          className="meal-selection-close-btn"
                          onClick={() => setShowRecommendations(prev => ({ ...prev, [mealType]: false }))}
                          whileHover={{ scale: 1.1, rotate: 90 }}
                          whileTap={{ scale: 0.9 }}
                          title="Close"
                        >
                          ✕
                        </motion.button>
                      </div>
                    </div>
                    
                    {/* Main Content Area - Two Column Layout */}
                    <div className="meal-selection-content">
                      {/* Left Sidebar - Search and Filters */}
                      <div className="meal-selection-sidebar">
                        {/* Search Section */}
                        <div className="meal-selection-search-section">
                          <label className="meal-selection-label">Search Meals</label>
                          <div className="meal-selection-search-box-large">
                            <span className="search-icon-large">🔍</span>
                            <input
                              type="text"
                              placeholder="Search by name, ingredient, or nutrient..."
                              value={recommendationSearch[mealType] || ''}
                              onChange={(e) => {
                                setRecommendationSearch(prev => ({
                                  ...prev,
                                  [mealType]: e.target.value
                                }));
                              }}
                              className="meal-selection-search-input-large"
                            />
                            {(recommendationSearch[mealType] || '').length > 0 && (
                              <motion.button
                                className="clear-search-btn-large"
                                onClick={() => {
                                  setRecommendationSearch(prev => ({
                                    ...prev,
                                    [mealType]: ''
                                  }));
                                }}
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                title="Clear search"
                              >
                                ✕
                              </motion.button>
                            )}
                          </div>
                        </div>
                        
                        {/* Filter Section */}
                        <div className="meal-selection-filter-section">
                          <label className="meal-selection-label">Filter by Category</label>
                          <div className="meal-selection-filters-grid">
                            {[
                              { id: 'all', label: 'All Meals', icon: '🍽️', color: '#667eea' },
                              { id: 'quick', label: 'Quick', icon: '⚡', color: '#f093fb' },
                              { id: 'high_protein', label: 'High Protein', icon: '💪', color: '#feca57' },
                              { id: 'high_iron', label: 'High Iron', icon: '🔴', color: '#ee5a6f' },
                              { id: 'omega3', label: 'Omega-3', icon: '🐟', color: '#339af0' },
                              { id: 'vegetarian', label: 'Vegetarian', icon: '🥗', color: '#6bcf7f' },
                              { id: 'vegan', label: 'Vegan', icon: '🌱', color: '#51cf66' }
                            ].map(filter => (
                              <motion.button
                                key={filter.id}
                                className={`meal-selection-filter-card ${(recommendationFilter[mealType] || 'all') === filter.id ? 'active' : ''}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                                  setRecommendationFilter(prev => ({
                                    ...prev,
                                    [mealType]: filter.id
                                  }));
                                }}
                                whileHover={{ scale: 1.05, y: -2 }}
                                whileTap={{ scale: 0.95 }}
                                style={{
                                  '--filter-color': filter.color
                                }}
                              >
                                <span className="filter-icon-large">{filter.icon}</span>
                                <span className="filter-label-large">{filter.label}</span>
                              </motion.button>
                            ))}
                  </div>
                        </div>
                        
                        {/* Results Count */}
                        <div className="meal-selection-results-count">
                          <span className="results-count-number">
                            {(() => {
                              const allMeals = recommendations[mealType] || [];
                              const searchQuery = recommendationSearch[mealType] || '';
                              const filter = recommendationFilter[mealType] || 'all';
                              let filtered = allMeals;
                              
                              if (searchQuery) {
                                filtered = filtered.filter(meal =>
                                  meal.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                  meal.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                  meal.ingredients?.some(ing => ing.toLowerCase().includes(searchQuery.toLowerCase()))
                                );
                              }
                              
                              if (filter !== 'all') {
                                filtered = filtered.filter(meal => {
                                  // Create a comprehensive text search string for fallback
                                  const mealText = [
                                    meal.name || '',
                                    meal.description || '',
                                    ...(meal.ingredients || []),
                                    ...(meal.nutrients || []),
                                    meal.tags?.join(' ') || ''
                                  ].join(' ').toLowerCase();
                                  
                                  switch(filter) {
                                    case 'quick': 
                                      if (meal.prepTimeMinutes && meal.prepTimeMinutes <= 15) return true;
                                      if (meal.prepTime) {
                                        const timeMatch = meal.prepTime.match(/(\d+)/);
                                        if (timeMatch && parseInt(timeMatch[1]) <= 15) return true;
                                      }
                                      return mealText.includes('quick') || meal.tags?.some(t => t.toLowerCase().includes('quick'));
                                      
                                    case 'high_protein': 
                                      if (meal.nutrients?.some(n => n.toLowerCase().includes('protein'))) return true;
                                      if (meal.protein || meal.tags?.some(t => t.toLowerCase().includes('protein'))) return true;
                                      const proteinFoods = ['chicken', 'beef', 'turkey', 'salmon', 'tuna', 'eggs', 'lentils', 'beans', 'tofu', 'quinoa', 'greek yogurt', 'yogurt'];
                                      return proteinFoods.some(food => mealText.includes(food));
                                      
                                    case 'high_iron': 
                                      if (meal.nutrients?.some(n => n.toLowerCase().includes('iron'))) return true;
                                      if (meal.iron || meal.tags?.some(t => t.toLowerCase().includes('iron'))) return true;
                                      const ironFoods = ['spinach', 'lentils', 'beans', 'beef', 'chicken', 'turkey', 'tofu', 'quinoa', 'fortified'];
                                      return ironFoods.some(food => mealText.includes(food));
                                      
                                    case 'omega3': 
                                      if (meal.nutrients?.some(n => n.toLowerCase().includes('omega'))) return true;
                                      if (meal.omega3 || meal.tags?.some(t => t.toLowerCase().includes('omega'))) return true;
                                      const omegaFoods = ['salmon', 'tuna', 'sardines', 'walnut', 'walnuts', 'chia', 'flax', 'flaxseed', 'cod', 'mackerel'];
                                      return omegaFoods.some(food => mealText.includes(food));
                                      
                                    case 'vegetarian': 
                                      const meatWords = ['chicken', 'beef', 'meat', 'turkey', 'pork', 'lamb', 'salmon', 'tuna', 'fish', 'seafood', 'shrimp', 'crab'];
                                      return !meatWords.some(word => mealText.includes(word));
                                      
                                    case 'vegan': 
                                      const animalProducts = ['chicken', 'beef', 'meat', 'turkey', 'pork', 'lamb', 'salmon', 'tuna', 'fish', 'seafood', 
                                                              'egg', 'eggs', 'dairy', 'cheese', 'milk', 'yogurt', 'butter', 'honey'];
                                      return !animalProducts.some(word => mealText.includes(word));
                                      
                                    default: return true;
                                  }
                                });
                              }
                              
                              return filtered.length;
                            })()}
                          </span>
                          <span className="results-count-text">meals available</span>
                        </div>
                      </div>
                      
                      {/* Right Content - Meal List */}
                      <div className="meal-selection-main">
                  
                        {/* Meal List - Spacious Grid */}
                        <div className="meal-selection-list">
                    {loading ? (
                      <div className="meal-selection-loading">
                        <motion.div
                          className="meal-selection-loading-spinner"
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        >
                          ⏳
                        </motion.div>
                        <p style={{ fontSize: '1.125rem', color: 'rgba(58, 48, 85, 0.7)', margin: 0 }}>
                          Loading amazing {mealType} suggestions...
                        </p>
                      </div>
                    ) : (() => {
                      // Apply search and filter
                      let filteredMeals = [...(recommendations[mealType] || [])];
                      const searchQuery = recommendationSearch[mealType] || '';
                      const filter = recommendationFilter[mealType] || 'all';
                      
                      // Apply search
                      if (searchQuery) {
                        filteredMeals = filteredMeals.filter(meal =>
                          meal.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          meal.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          meal.ingredients?.some(ing => ing.toLowerCase().includes(searchQuery.toLowerCase()))
                        );
                      }
                      
                      // Apply filter
                      if (filter !== 'all') {
                        filteredMeals = filteredMeals.filter(meal => {
                          // Create a comprehensive text search string for fallback
                          const mealText = [
                            meal.name || '',
                            meal.description || '',
                            ...(meal.ingredients || []),
                            ...(meal.nutrients || []),
                            meal.tags?.join(' ') || ''
                          ].join(' ').toLowerCase();
                          
                          switch(filter) {
                            case 'quick': 
                              // Check prepTimeMinutes first, then parse prepTime string
                              if (meal.prepTimeMinutes && meal.prepTimeMinutes <= 15) return true;
                              if (meal.prepTime) {
                                const timeMatch = meal.prepTime.match(/(\d+)/);
                                if (timeMatch && parseInt(timeMatch[1]) <= 15) return true;
                              }
                              return mealText.includes('quick') || meal.tags?.some(t => t.toLowerCase().includes('quick'));
                              
                            case 'high_protein': 
                              // Check nutrients array, direct property, or tags
                              if (meal.nutrients?.some(n => n.toLowerCase().includes('protein'))) return true;
                              if (meal.protein || meal.tags?.some(t => t.toLowerCase().includes('protein'))) return true;
                              // Check ingredients for protein-rich foods
                              const proteinFoods = ['chicken', 'beef', 'turkey', 'salmon', 'tuna', 'eggs', 'lentils', 'beans', 'tofu', 'quinoa', 'greek yogurt', 'yogurt'];
                              return proteinFoods.some(food => mealText.includes(food));
                              
                            case 'high_iron': 
                              // Check nutrients array, direct property, or tags
                              if (meal.nutrients?.some(n => n.toLowerCase().includes('iron'))) return true;
                              if (meal.iron || meal.tags?.some(t => t.toLowerCase().includes('iron'))) return true;
                              // Check ingredients for iron-rich foods
                              const ironFoods = ['spinach', 'lentils', 'beans', 'beef', 'chicken', 'turkey', 'tofu', 'quinoa', 'fortified'];
                              return ironFoods.some(food => mealText.includes(food));
                              
                            case 'omega3': 
                              // Check nutrients array or tags
                              if (meal.nutrients?.some(n => n.toLowerCase().includes('omega'))) return true;
                              if (meal.omega3 || meal.tags?.some(t => t.toLowerCase().includes('omega'))) return true;
                              // Check ingredients for omega-3 rich foods
                              const omegaFoods = ['salmon', 'tuna', 'sardines', 'walnut', 'walnuts', 'chia', 'flax', 'flaxseed', 'cod', 'mackerel'];
                              return omegaFoods.some(food => mealText.includes(food));
                              
                            case 'vegetarian': 
                              // Exclude meat and fish
                              const meatWords = ['chicken', 'beef', 'meat', 'turkey', 'pork', 'lamb', 'salmon', 'tuna', 'fish', 'seafood', 'shrimp', 'crab'];
                              return !meatWords.some(word => mealText.includes(word));
                              
                            case 'vegan': 
                              // Exclude all animal products
                              const animalProducts = ['chicken', 'beef', 'meat', 'turkey', 'pork', 'lamb', 'salmon', 'tuna', 'fish', 'seafood', 
                                                      'egg', 'eggs', 'dairy', 'cheese', 'milk', 'yogurt', 'butter', 'honey'];
                              return !animalProducts.some(word => mealText.includes(word));
                              
                            default: return true;
                          }
                        });
                      }
                      
                        // Separate filtered and non-filtered meals
                        const regularMeals = [];
                      const filteredMealsList = [];
                        
                      filteredMeals.forEach((meal, index) => {
                          if (meal.isFiltered) {
                          filteredMealsList.push({ meal, index });
                          } else {
                            regularMeals.push({ meal, index });
                          }
                        });
                      
                      if (filteredMeals.length === 0) {
                        return (
                          <div className="meal-selection-empty">
                            <div className="meal-selection-empty-icon">🍽️</div>
                            <h3 className="meal-selection-empty-title">No meals found</h3>
                            <p className="meal-selection-empty-message">
                              {searchQuery 
                                ? `No meals match "${searchQuery}". Try a different search term or clear your search.`
                                : `No meals found for this filter. Try selecting a different category.`}
                            </p>
                            {(searchQuery || filter !== 'all') && (
                              <motion.button
                                className="meal-selection-clear-filters-btn"
                                onClick={() => {
                                  setRecommendationSearch(prev => ({ ...prev, [mealType]: '' }));
                                  setRecommendationFilter(prev => ({ ...prev, [mealType]: 'all' }));
                                }}
                                whileHover={{ scale: 1.05, y: -2 }}
                                whileTap={{ scale: 0.95 }}
                              >
                                🔄 Clear All Filters
                              </motion.button>
                            )}
                          </div>
                        );
                      }
                        
                        return (
                          <>
                            {/* Regular meals first */}
                            {regularMeals.map(({ meal, index }) => {
                              const mealId = meal.id || meal.name || `meal-${index}`;
                              return (
                                <motion.div
                                  key={mealId}
                                className={`meal-selection-card ${meal.hasAllergens ? 'has-allergens' : ''}`}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.05 }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                  console.log('🖱️ Meal card clicked:', { mealName: meal.name, mealType, meal });
                                    handleMealSelect(mealType, meal);
                                  }}
                                whileHover={{ scale: 1.02, y: -4 }}
                                  whileTap={{ scale: 0.98 }}
                                  style={{ cursor: 'pointer' }}
                                  tabIndex={0}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      handleMealSelect(mealType, meal);
                                    }
                                  }}
                                >
                                <div className={`meal-selection-card-content ${meal.hasAllergens ? 'has-allergen-caution' : ''}`}>
                                  <div className="meal-selection-card-header">
                                    <h3 className="meal-selection-card-title">{meal.name}</h3>
                                    <div className="meal-selection-card-badges">
                                      {meal.hasAllergens && (
                                        <span className="meal-selection-allergen-badge" title={meal.cautionText || `Contains: ${(meal.allergens || []).join(', ')}`}>
                                          ⚠️ Contains your allergen(s)
                                        </span>
                                      )}
                                      {meal.prepTimeMinutes && (
                                        <span className="meal-selection-card-time">
                                          ⏱️ {meal.prepTimeMinutes} min
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  {(meal.whyRecommended || (meal.matchReasons && meal.matchReasons.length > 0)) && (
                                    <p className="meal-selection-why">
                                      {meal.whyRecommended
                                        ? (meal.whyRecommended.length > 100 ? meal.whyRecommended.substring(0, 100) + '…' : meal.whyRecommended)
                                        : meal.matchReasons[0] + (meal.matchReasons.length > 1 ? ' (+' + (meal.matchReasons.length - 1) + ' more)' : '')}
                                    </p>
                                  )}
                                  {(() => {
                                    const notes = meal.considerations?.length > 0 ? meal.considerations : (meal.filterReasons || []);
                                    const label = meal.isFiltered ? 'Why we still show it' : 'Things to note';
                                    return notes.length > 0 ? (
                                      <p className="meal-selection-consideration" title={notes.join(' • ')}>
                                        <span className="meal-selection-consideration-icon">📌</span> <strong>{label}:</strong> {notes[0]}{notes.length > 1 ? ` (+${notes.length - 1} more)` : ''}
                                      </p>
                                    ) : null;
                                  })()}
                                  {meal.description && (
                                    <p className="meal-selection-card-description">
                                      {meal.description.substring(0, 120)}{meal.description.length > 120 ? '…' : ''}
                                    </p>
                                  )}
                                  {meal.nutrients && meal.nutrients.length > 0 && (
                                    <div className="meal-selection-card-nutrients">
                                      {meal.nutrients.slice(0, 3).map((nutrient, idx) => (
                                        <span key={idx} className="meal-selection-nutrient-badge">
                                          {nutrient}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                  <motion.button
                                    className="meal-selection-add-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleMealSelect(mealType, meal);
                                    }}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                  >
                                    ➕ Add to {mealType.charAt(0).toUpperCase() + mealType.slice(1)}
                                  </motion.button>
                                </div>
                                </motion.div>
                              );
                            })}
                            
                            {/* Filtered meals with special card */}
                          {filteredMealsList.length > 0 && (
                            <div className="meal-selection-filtered-section">
                              <div className="meal-selection-filtered-header">
                                  <span className="filtered-meals-icon">🔍</span>
                                <div>
                                  <h3 className="filtered-meals-title">Other Options</h3>
                                  <p className="filtered-meals-subtitle">These meals may not match all your preferences</p>
                                </div>
                              </div>
                              {filteredMealsList.map(({ meal, index }) => {
                                  const mealId = meal.id || meal.name || `filtered-meal-${index}`;
                                  return (
                                    <motion.div
                                      key={mealId}
                                    className={`meal-selection-card filtered ${meal.hasAllergens ? 'has-allergens' : ''}`}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 0.9, y: 0 }}
                                    transition={{ delay: index * 0.05 }}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        console.log('🖱️ Filtered meal clicked:', { mealName: meal.name, mealType, meal });
                                        handleMealSelect(mealType, meal);
                                      }}
                                    whileHover={{ scale: 1.01, y: -2 }}
                                      whileTap={{ scale: 0.99 }}
                                      style={{ cursor: 'pointer' }}
                                      tabIndex={0}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                          e.preventDefault();
                                          handleMealSelect(mealType, meal);
                                        }
                                      }}
                                    >
                                    <div className={`meal-selection-card-content ${meal.hasAllergens ? 'has-allergen-caution' : ''}`}>
                                      <div className="meal-selection-card-header">
                                        <h3 className="meal-selection-card-title">{meal.name}</h3>
                                        <div className="meal-selection-card-badges">
                                          {meal.hasAllergens && (
                                            <span className="meal-selection-allergen-badge">⚠️ Contains your allergen(s)</span>
                                          )}
                                          <span className="meal-selection-filtered-badge">⚠️ May not match preferences</span>
                                        </div>
                                      </div>
                                      {(meal.whyRecommended || (meal.matchReasons && meal.matchReasons.length > 0)) && (
                                        <p className="meal-selection-why">
                                          {meal.whyRecommended ? (meal.whyRecommended.length > 100 ? meal.whyRecommended.substring(0, 100) + '…' : meal.whyRecommended) : meal.matchReasons[0]}
                                        </p>
                                      )}
                                      {(() => {
                                        const notes = meal.considerations?.length > 0 ? meal.considerations : (meal.filterReasons || []);
                                        const label = meal.isFiltered ? 'Why we still show it' : 'Things to note';
                                        return notes.length > 0 ? (
                                          <p className="meal-selection-consideration" title={notes.join(' • ')}>
                                            <span className="meal-selection-consideration-icon">📌</span> <strong>{label}:</strong> {notes[0]}{notes.length > 1 ? ` (+${notes.length - 1} more)` : ''}
                                          </p>
                                        ) : null;
                                      })()}
                                      {meal.description && (
                                        <p className="meal-selection-card-description">
                                          {meal.description.substring(0, 120)}{meal.description.length > 120 ? '…' : ''}
                                        </p>
                                      )}
                                      <motion.button
                                        className="meal-selection-add-btn"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleMealSelect(mealType, meal);
                                        }}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                      >
                                        ➕ Add to {mealType.charAt(0).toUpperCase() + mealType.slice(1)}
                                      </motion.button>
                                    </div>
                                    </motion.div>
                                  );
                                })}
                              </div>
                            )}
                          </>
                        );
                    })()}
                      </div>
                      </div>
                  </div>
                    </motion.div>
                </motion.div>
              )}
              </AnimatePresence>,
              document.body
            )}
          </motion.div>
        ))}
      </div>
      
      {/* Hydration Tracker */}
      <div className="hydration-tracker">
        <div className="hydration-header">
          <span className="hydration-icon">💧</span>
          <div>
            <h3>Hydration Goal</h3>
            <p>8 cups per day</p>
          </div>
        </div>
        <div className="hydration-progress">
          <div className="hydration-cups">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((cup) => (
              <motion.button
                key={cup}
                className={`hydration-cup ${hydrationCups >= cup ? 'filled' : ''}`}
                onClick={() => {
                  // Toggle: if clicking the same cup, decrease by 1
                  setHydrationCups(prev => prev === cup ? cup - 1 : cup);
                }}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                💧
              </motion.button>
            ))}
          </div>
          <p className="hydration-text">
            {hydrationCups} / 8 cups today
            {hydrationCups >= 8 ? (
              <span className="encouragement success"> Excellent! You've met your hydration goal today! 🎉</span>
            ) : hydrationCups >= 6 ? (
              <span className="encouragement warning"> Almost there! Just {8 - hydrationCups} more cup{8 - hydrationCups > 1 ? 's' : ''} to go! 💪</span>
            ) : hydrationCups >= 4 ? (
              <span className="encouragement info"> You're doing great! Keep hydrating - {8 - hydrationCups} more cups to reach your goal 💕</span>
            ) : hydrationCups >= 2 ? (
              <span className="encouragement info"> Good start! Remember to drink water throughout the day - {8 - hydrationCups} more cups needed 💧</span>
            ) : hydrationCups >= 1 ? (
              <span className="encouragement info"> Every cup counts! Try to drink {8 - hydrationCups} more cups today 🌊</span>
            ) : (
              <span className="encouragement info"> Start your day with hydration! Aim for 8 cups of water today 💧</span>
            )}
          </p>
        </div>
      </div>
      
      {/* Nutrient Summary */}
      {nutrientGoals && (() => {
        const currentIntake = calculateCurrentIntake();
        return (
          <div className="nutrient-summary">
            <h3>Today's Nutrient Progress</h3>
            <div className="nutrient-bars">
              {Object.entries(nutrientGoals).map(([nutrient, goal]) => {
                const current = currentIntake[nutrient] || 0;
                const progress = Math.min((current / goal) * 100, 100);
                const unit = nutrient === 'protein' || nutrient === 'fiber' ? 'g' : 
                            nutrient === 'iron' || nutrient === 'calcium' ? 'mg' : 
                            nutrient === 'omega3' ? 'mg' : 'mcg';
                // Format nutrient name for display (handle omega3 -> Omega-3)
                const getNutrientDisplayName = (nut) => {
                  if (nut === 'omega3') return 'Omega-3';
                  return nut.charAt(0).toUpperCase() + nut.slice(1);
                };
                
                return (
                  <div key={nutrient} className="nutrient-bar-item">
                    <div className="nutrient-label">
                      <span>{getNutrientDisplayName(nutrient)}</span>
                      <span>{current.toFixed(1)} / {goal} {unit}</span>
                    </div>
                    <div className="nutrient-bar">
                      <motion.div
                        className="nutrient-fill"
                        initial={{ width: 0 }}
                        animate={{ width: `${progress}%` }}
                        style={{
                          background: progress >= 80 ? 'linear-gradient(90deg, #4CAF50, #66BB6A)' :
                                     progress >= 50 ? 'linear-gradient(90deg, #FFC107, #FFB300)' :
                                     'linear-gradient(90deg, #E91E63, #EC407A)'
                        }}
                      />
                    </div>
                    {progress >= 100 && (
                      <p className="nutrient-encouragement" style={{ color: '#4CAF50' }}>
                        Perfect! You've exceeded your {nutrient} goal today! 🎉
                      </p>
                    )}
                    {progress >= 80 && progress < 100 && (
                      <p className="nutrient-encouragement" style={{ color: '#4CAF50' }}>
                        Excellent! You're meeting your {nutrient} needs! 💪
                      </p>
                    )}
                    {progress >= 50 && progress < 80 && (
                      <>
                        <p className="nutrient-encouragement" style={{ color: '#FFC107' }}>
                          You're close to meeting your {nutrient} needs today! Just {((goal - current) / goal * 100).toFixed(0)}% more to go! ✨
                        </p>
                        <motion.button
                          className="add-nutrient-meals-button"
                          onClick={() => loadNutrientRichMeals(nutrient)}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          style={{
                            marginTop: '8px',
                            padding: '8px 16px',
                            background: 'linear-gradient(135deg, #E91E63, #EC407A)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            fontFamily: 'var(--font-body)',
                            boxShadow: '0 2px 8px rgba(233, 30, 99, 0.3)'
                          }}
                        >
                          ➕ Add {nutrient}-rich meals
                        </motion.button>
                      </>
                    )}
                    {progress >= 25 && progress < 50 && (
                      <>
                        <p className="nutrient-encouragement">
                          Good progress on {nutrient}! You're at {progress.toFixed(0)}% - keep adding {nutrient}-rich foods! 💕
                        </p>
                        <motion.button
                          className="add-nutrient-meals-button"
                          onClick={() => loadNutrientRichMeals(nutrient)}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          style={{
                            marginTop: '8px',
                            padding: '8px 16px',
                            background: 'linear-gradient(135deg, #E91E63, #EC407A)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            fontFamily: 'var(--font-body)',
                            boxShadow: '0 2px 8px rgba(233, 30, 99, 0.3)'
                          }}
                        >
                          ➕ Add {nutrient}-rich meals
                        </motion.button>
                      </>
                    )}
                    {progress > 0 && progress < 25 && (
                      <>
                        <p className="nutrient-encouragement">
                          Every bit of {nutrient} helps! You're at {progress.toFixed(0)}% - try adding more {nutrient}-rich foods today 🌱
                        </p>
                        <motion.button
                          className="add-nutrient-meals-button"
                          onClick={() => loadNutrientRichMeals(nutrient)}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          style={{
                            marginTop: '8px',
                            padding: '8px 16px',
                            background: 'linear-gradient(135deg, #E91E63, #EC407A)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            fontFamily: 'var(--font-body)',
                            boxShadow: '0 2px 8px rgba(233, 30, 99, 0.3)'
                          }}
                        >
                          ➕ Add {nutrient}-rich meals
                        </motion.button>
                      </>
                    )}
                    {progress === 0 && (
                      <>
                        <p className="nutrient-encouragement">
                          Start tracking your {nutrient} intake! Add meals to see your progress 📊
                        </p>
                        <motion.button
                          className="add-nutrient-meals-button"
                          onClick={() => loadNutrientRichMeals(nutrient)}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          style={{
                            marginTop: '8px',
                            padding: '8px 16px',
                            background: 'linear-gradient(135deg, #E91E63, #EC407A)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            fontFamily: 'var(--font-body)',
                            boxShadow: '0 2px 8px rgba(233, 30, 99, 0.3)'
                          }}
                        >
                          ➕ Add {nutrient}-rich meals
                        </motion.button>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}
      
      {/* Meal Information Modal - Shows why meal is recommended when added */}
      {showMealInfoModal && selectedMealInfo && createPortal(
        <AnimatePresence>
          <motion.div
            className="meal-info-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => {
              setShowMealInfoModal(false);
              setSelectedMealInfo(null);
            }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 99999, // Very high z-index to ensure it's on top
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              padding: '20px',
              paddingTop: `${headerHeight}px`, // Dynamic padding based on actual header height
              overflowY: 'auto'
            }}
          >
            <motion.div
              className="meal-info-modal"
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'white',
                borderRadius: '20px',
                padding: '30px',
                maxWidth: '600px',
                width: '100%',
                maxHeight: `calc(100vh - ${headerHeight + 40}px)`, // Account for header and padding
                overflowY: 'auto',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
                marginTop: '20px' // Small top margin for spacing
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ margin: 0, color: '#3A3055', fontSize: '1.5rem' }}>
                  {selectedMealInfo.mealType === 'breakfast' && '🌅'}
                  {selectedMealInfo.mealType === 'lunch' && '☀️'}
                  {selectedMealInfo.mealType === 'dinner' && '🌙'}
                  {selectedMealInfo.mealType === 'snack' && '🍎'}
                  {' '}
                  {selectedMealInfo.meal.name} Added!
                </h3>
                <button
                  onClick={() => {
                    setShowMealInfoModal(false);
                    setSelectedMealInfo(null);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: '#999',
                    padding: '0',
                    width: '30px',
                    height: '30px'
                  }}
                >
                  ✕
                </button>
              </div>
              
              {/* Meal Description */}
              {selectedMealInfo.meal.description && (
                <div style={{
                  padding: '16px',
                  background: 'linear-gradient(135deg, rgba(247, 207, 230, 0.1) 0%, rgba(232, 164, 199, 0.05) 100%)',
                  borderRadius: '12px',
                  marginBottom: '20px',
                  border: '1px solid rgba(232, 164, 199, 0.2)'
                }}>
                  <p style={{ margin: 0, color: '#3A3055', lineHeight: 1.6, fontSize: '0.95rem' }}>
                    {selectedMealInfo.meal.description}
                  </p>
                </div>
              )}

              {/* Why This Meal Was Recommended - Based on Your Profile */}
              <div style={{ marginTop: '20px' }}>
                <h4 style={{ 
                  margin: '0 0 16px 0', 
                  fontSize: '18px', 
                  fontWeight: 700, 
                  color: '#3A3055',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <span>✨</span>
                  <span>Why This Meal Was Recommended</span>
                </h4>
                
                {/* Personalized Reasons Based on Profile */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {/* Pregnancy Week/Trimester */}
                  {profile && (() => {
                    let currentWeek = null;
                    let trimester = null;
                    if (profile.currentWeek) {
                      currentWeek = profile.currentWeek;
                      trimester = currentWeek <= 13 ? 1 : currentWeek <= 27 ? 2 : 3;
                    } else if (profile.lmpDate) {
                      const lmpDate = new Date(profile.lmpDate);
                      const today = new Date();
                      const diffTime = today - lmpDate;
                      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
                      currentWeek = Math.floor(diffDays / 7) + 1;
                      trimester = currentWeek <= 13 ? 1 : currentWeek <= 27 ? 2 : 3;
                    }
                    
                    if (currentWeek) {
                      return (
                        <div style={{
                          padding: '12px 16px',
                          background: 'rgba(233, 30, 99, 0.08)',
                          borderRadius: '10px',
                          border: '1px solid rgba(233, 30, 99, 0.2)'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                            <span style={{ fontSize: '18px' }}>🤰</span>
                            <strong style={{ color: '#3A3055', fontSize: '14px' }}>Pregnancy Week {currentWeek} (Trimester {trimester})</strong>
                          </div>
                          <p style={{ margin: 0, color: '#5A4A6A', fontSize: '13px', lineHeight: 1.5 }}>
                            This meal provides nutrients essential for {trimester === 1 ? 'early development and neural tube formation' : trimester === 2 ? 'rapid baby growth and bone development' : 'final growth spurt and preparation for birth'}.
                          </p>
                        </div>
                      );
                    }
                    return null;
                  })()}

                  {/* Allergies - Safety Check */}
                  {userPreferences?.allergies && userPreferences.allergies.length > 0 && (
                    <div style={{
                      padding: '12px 16px',
                      background: 'rgba(76, 175, 80, 0.08)',
                      borderRadius: '10px',
                      border: '1px solid rgba(76, 175, 80, 0.2)'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <span style={{ fontSize: '18px' }}>✅</span>
                        <strong style={{ color: '#3A3055', fontSize: '14px' }}>Allergy Safe</strong>
                      </div>
                      <p style={{ margin: 0, color: '#5A4A6A', fontSize: '13px', lineHeight: 1.5 }}>
                        This meal is safe for you - it doesn't contain any of your allergens: <strong>{userPreferences.allergies.join(', ')}</strong>
                      </p>
                    </div>
                  )}

                  {/* Food Preferences Match */}
                  {userPreferences?.foodPreferences && userPreferences.foodPreferences.length > 0 && (
                    <div style={{
                      padding: '12px 16px',
                      background: 'rgba(233, 30, 99, 0.08)',
                      borderRadius: '10px',
                      border: '1px solid rgba(233, 30, 99, 0.2)'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <span style={{ fontSize: '18px' }}>❤️</span>
                        <strong style={{ color: '#3A3055', fontSize: '14px' }}>Matches Your Taste Preferences</strong>
                      </div>
                      <p style={{ margin: 0, color: '#5A4A6A', fontSize: '13px', lineHeight: 1.5 }}>
                        This meal aligns with your preferences: <strong>{userPreferences.foodPreferences.join(', ')}</strong>
                      </p>
                    </div>
                  )}

                  {/* Morning Sickness Consideration */}
                  {userPreferences?.morningSicknessSeverity && userPreferences.morningSicknessSeverity !== 'NONE' && (
                    <div style={{
                      padding: '12px 16px',
                      background: 'rgba(255, 193, 7, 0.08)',
                      borderRadius: '10px',
                      border: '1px solid rgba(255, 193, 7, 0.2)'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <span style={{ fontSize: '18px' }}>🤢</span>
                        <strong style={{ color: '#3A3055', fontSize: '14px' }}>Nausea-Friendly</strong>
                      </div>
                      <p style={{ margin: 0, color: '#5A4A6A', fontSize: '13px', lineHeight: 1.5 }}>
                        Selected because it's gentle on your stomach and easy to digest, perfect for managing your {userPreferences.morningSicknessSeverity.toLowerCase()} morning sickness.
                      </p>
                    </div>
                  )}

                  {/* Energy Level Support */}
                  {userPreferences?.energyLevel && userPreferences.energyLevel === 'LOW' && (
                    <div style={{
                      padding: '12px 16px',
                      background: 'rgba(33, 150, 243, 0.08)',
                      borderRadius: '10px',
                      border: '1px solid rgba(33, 150, 243, 0.2)'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <span style={{ fontSize: '18px' }}>⚡</span>
                        <strong style={{ color: '#3A3055', fontSize: '14px' }}>Energy-Boosting</strong>
                      </div>
                      <p style={{ margin: 0, color: '#5A4A6A', fontSize: '13px', lineHeight: 1.5 }}>
                        Rich in iron and protein to help combat fatigue and boost your energy levels naturally.
                      </p>
                    </div>
                  )}

                  {/* Cooking Time Match */}
                  {userPreferences?.cookingTime && (
                    <div style={{
                      padding: '12px 16px',
                      background: 'rgba(156, 39, 176, 0.08)',
                      borderRadius: '10px',
                      border: '1px solid rgba(156, 39, 176, 0.2)'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <span style={{ fontSize: '18px' }}>⏱️</span>
                        <strong style={{ color: '#3A3055', fontSize: '14px' }}>Fits Your Schedule</strong>
                      </div>
                      <p style={{ margin: 0, color: '#5A4A6A', fontSize: '13px', lineHeight: 1.5 }}>
                        Can be prepared in {userPreferences.cookingTime === 'QUICK' ? '15 minutes or less' : userPreferences.cookingTime === 'MODERATE' ? 'around 30 minutes' : '1 hour or more'}, matching your available cooking time.
                      </p>
                    </div>
                  )}

                  {/* Cooking Skill Level */}
                  {userPreferences?.cookingSkill === 'BEGINNER' && (
                    <div style={{
                      padding: '12px 16px',
                      background: 'rgba(255, 152, 0, 0.08)',
                      borderRadius: '10px',
                      border: '1px solid rgba(255, 152, 0, 0.2)'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <span style={{ fontSize: '18px' }}>👩‍🍳</span>
                        <strong style={{ color: '#3A3055', fontSize: '14px' }}>Beginner-Friendly</strong>
                      </div>
                      <p style={{ margin: 0, color: '#5A4A6A', fontSize: '13px', lineHeight: 1.5 }}>
                        Simple recipe with clear instructions, perfect for your cooking skill level.
                      </p>
                    </div>
                  )}

                  {/* Medical Conditions */}
                  {userPreferences?.medicalConditions && (
                    <div style={{
                      padding: '12px 16px',
                      background: 'rgba(244, 67, 54, 0.08)',
                      borderRadius: '10px',
                      border: '1px solid rgba(244, 67, 54, 0.2)'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <span style={{ fontSize: '18px' }}>🏥</span>
                        <strong style={{ color: '#3A3055', fontSize: '14px' }}>Medical Considerations</strong>
                      </div>
                      <p style={{ margin: 0, color: '#5A4A6A', fontSize: '13px', lineHeight: 1.5 }}>
                        Selected with your medical condition ({userPreferences.medicalConditions}) in mind, ensuring it's safe and appropriate for your health needs.
                      </p>
                    </div>
                  )}

                  {/* Calorie Needs */}
                  {healthProfile?.dailyCalorieNeeds && (
                    <div style={{
                      padding: '12px 16px',
                      background: 'rgba(233, 30, 99, 0.08)',
                      borderRadius: '10px',
                      border: '1px solid rgba(233, 30, 99, 0.2)'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <span style={{ fontSize: '18px' }}>🔥</span>
                        <strong style={{ color: '#3A3055', fontSize: '14px' }}>Calorie Appropriate</strong>
                      </div>
                      <p style={{ margin: 0, color: '#5A4A6A', fontSize: '13px', lineHeight: 1.5 }}>
                        This meal fits into your daily calorie goal of approximately {Math.round(healthProfile.dailyCalorieNeeds)} calories per day.
                      </p>
                    </div>
                  )}

                  {/* Detailed Explanation if Available */}
                  {selectedMealInfo.meal.whyRecommended && (
                    <div style={{
                      padding: '16px',
                      background: 'linear-gradient(135deg, rgba(247, 207, 230, 0.15) 0%, rgba(232, 164, 199, 0.1) 100%)',
                      borderRadius: '12px',
                      border: '1px solid rgba(232, 164, 199, 0.3)',
                      marginTop: '8px'
                    }}>
                      <div style={{ marginBottom: '12px' }}>
                        <strong style={{ color: '#3A3055', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span>📋</span>
                          <span>Detailed Explanation</span>
                        </strong>
                      </div>
                      <div style={{ color: '#5A4A6A', fontSize: '13px', lineHeight: 1.6 }}>
                        {selectedMealInfo.meal.whyRecommended.split('\n').map((line, idx) => {
                          if (line.trim().startsWith('•')) {
                            return (
                              <div key={idx} style={{ marginBottom: '8px', paddingLeft: '8px' }}>
                                <span style={{ marginRight: '8px' }}>•</span>
                                {line.trim().substring(1).trim()}
                              </div>
                            );
                          } else if (line.trim()) {
                            return (
                              <p key={idx} style={{ margin: '8px 0' }}>
                                {line.trim()}
                              </p>
                            );
                          }
                          return null;
                        })}
                      </div>
                    </div>
                  )}

                  {/* Fallback if no specific reasons */}
                  {!selectedMealInfo.meal.whyRecommended && 
                   !profile && 
                   (!userPreferences?.allergies || userPreferences.allergies.length === 0) &&
                   (!userPreferences?.foodPreferences || userPreferences.foodPreferences.length === 0) &&
                   (!userPreferences?.morningSicknessSeverity || userPreferences.morningSicknessSeverity === 'NONE') &&
                   (!userPreferences?.energyLevel || userPreferences.energyLevel !== 'LOW') &&
                   !userPreferences?.cookingTime &&
                   userPreferences?.cookingSkill !== 'BEGINNER' &&
                   !userPreferences?.medicalConditions &&
                   !healthProfile?.dailyCalorieNeeds && (
                    <div style={{
                      padding: '16px',
                      background: 'linear-gradient(135deg, rgba(247, 207, 230, 0.15) 0%, rgba(232, 164, 199, 0.1) 100%)',
                      borderRadius: '12px',
                      border: '1px solid rgba(232, 164, 199, 0.2)'
                    }}>
                      <p style={{ margin: 0, color: '#5A4A6A', lineHeight: 1.6, fontSize: '13px' }}>
                        ✅ This meal has been added to your {selectedMealInfo.mealType} plan. It's been selected based on your preferences and pregnancy needs.
                      </p>
                    </div>
                  )}
                </div>
              </div>
              
              <div style={{ marginTop: '30px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <motion.button
                  onClick={() => {
                    setShowMealInfoModal(false);
                    setSelectedMealInfo(null);
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    padding: '12px 24px',
                    background: 'linear-gradient(135deg, #E91E63 0%, #C2185B 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(233, 30, 99, 0.3)'
                  }}
                >
                  Got it! 👍
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}

      {/* Meal Confirmation Modal */}
      {showMealConfirmationModal && confirmationMealData && createPortal(
        <AnimatePresence>
          <motion.div
            className="meal-confirmation-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={cancelAddMeal}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              zIndex: 10002,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px',
              paddingTop: `${headerHeight}px`,
              overflowY: 'auto'
            }}
          >
            <motion.div
              className="meal-confirmation-modal"
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'white',
                borderRadius: '24px',
                padding: '40px',
                maxWidth: '600px',
                width: '100%',
                maxHeight: `calc(100vh - ${headerHeight + 40}px)`,
                overflowY: 'auto',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                border: '3px solid rgba(233, 30, 99, 0.2)'
              }}
            >
              {/* Confirmation Header */}
              <div style={{ 
                textAlign: 'center',
                marginBottom: '32px'
              }}>
                <div style={{ fontSize: '4rem', marginBottom: '16px' }}>
                  {confirmationMealData.mealType === 'breakfast' && '🌅'}
                  {confirmationMealData.mealType === 'lunch' && '☀️'}
                  {confirmationMealData.mealType === 'dinner' && '🌙'}
                  {confirmationMealData.mealType === 'snack' && '🍎'}
                </div>
                <h2 style={{ 
                  margin: '0 0 8px 0', 
                  color: '#3A3055', 
                  fontSize: '1.75rem',
                  fontWeight: 700,
                  fontFamily: 'var(--font-body)'
                }}>
                  Add to {confirmationMealData.mealType.charAt(0).toUpperCase() + confirmationMealData.mealType.slice(1)}?
                </h2>
                <p style={{ 
                  margin: 0, 
                  color: 'rgba(58, 48, 85, 0.7)', 
                  fontSize: '1rem',
                  fontFamily: 'var(--font-body)'
                }}>
                  Confirm to add this meal to your daily plan
                </p>
              </div>
              
              {/* Meal Preview Card */}
              <div style={{
                background: 'linear-gradient(135deg, rgba(255, 250, 252, 0.8) 0%, rgba(255, 245, 250, 0.8) 100%)',
                borderRadius: '16px',
                padding: '24px',
                marginBottom: '32px',
                border: '2px solid rgba(233, 30, 99, 0.15)'
              }}>
                <h3 style={{ 
                  margin: '0 0 12px 0', 
                  color: '#3A3055', 
                  fontSize: '1.25rem',
                  fontWeight: 700,
                  fontFamily: 'var(--font-body)'
                }}>
                  {confirmationMealData.meal.name}
                </h3>
                {confirmationMealData.meal.description && (
                  <p style={{ 
                    margin: '0 0 16px 0', 
                    color: 'rgba(58, 48, 85, 0.8)', 
                    fontSize: '0.9375rem',
                    lineHeight: 1.6,
                    fontFamily: 'var(--font-body)'
                  }}>
                    {confirmationMealData.meal.description}
                  </p>
                )}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                  {confirmationMealData.meal.prepTimeMinutes && (
                    <span style={{
                      padding: '6px 12px',
                      background: 'rgba(233, 30, 99, 0.1)',
                      borderRadius: '12px',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      color: '#E91E63',
                      fontFamily: 'var(--font-body)'
                    }}>
                      ⏱️ {confirmationMealData.meal.prepTimeMinutes} min
                    </span>
                  )}
                  {confirmationMealData.meal.calories && (
                    <span style={{
                      padding: '6px 12px',
                      background: 'rgba(233, 30, 99, 0.1)',
                      borderRadius: '12px',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      color: '#E91E63',
                      fontFamily: 'var(--font-body)'
                    }}>
                      🔥 {confirmationMealData.meal.calories} cal
                    </span>
                  )}
                </div>
                {confirmationMealData.meal.nutrients && confirmationMealData.meal.nutrients.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {confirmationMealData.meal.nutrients.slice(0, 4).map((nutrient, idx) => (
                      <span key={idx} style={{
                        padding: '4px 10px',
                        background: 'rgba(233, 30, 99, 0.08)',
                        borderRadius: '10px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: '#E91E63',
                        fontFamily: 'var(--font-body)'
                      }}>
                        {nutrient}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Action Buttons */}
              <div style={{ 
                display: 'flex', 
                gap: '16px', 
                justifyContent: 'center'
              }}>
                <motion.button
                  onClick={cancelAddMeal}
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    padding: '14px 32px',
                    background: '#E0E0E0',
                    color: '#424242',
                    border: 'none',
                    borderRadius: '16px',
                    fontSize: '1rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    fontFamily: 'var(--font-body)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Cancel
                </motion.button>
                <motion.button
                  onClick={confirmAddMeal}
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    padding: '14px 32px',
                    background: 'linear-gradient(135deg, #E91E63 0%, #EC407A 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '16px',
                    fontSize: '1rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    fontFamily: 'var(--font-body)',
                    boxShadow: '0 4px 16px rgba(233, 30, 99, 0.4)',
                    transition: 'all 0.2s ease'
                  }}
                >
                  ✅ Yes, Add It!
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}

      {/* Allergy Warning Modal */}
      {showAllergyWarningModal && allergyWarningData && createPortal(
        <AnimatePresence>
          <motion.div
            className="allergy-warning-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => {
              setShowAllergyWarningModal(false);
              setAllergyWarningData(null);
            }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              zIndex: 99999,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              padding: '20px',
              paddingTop: `${headerHeight}px`,
              overflowY: 'auto'
            }}
          >
            <motion.div
              className="allergy-warning-modal"
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'white',
                borderRadius: '20px',
                padding: '30px',
                maxWidth: '600px',
                width: '100%',
                maxHeight: `calc(100vh - ${headerHeight + 40}px)`,
                overflowY: 'auto',
                boxShadow: '0 10px 40px rgba(244, 67, 54, 0.3)',
                border: '3px solid #F44336'
              }}
            >
              {/* Warning Header */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px', 
                marginBottom: '20px',
                padding: '16px',
                background: 'linear-gradient(135deg, rgba(244, 67, 54, 0.1) 0%, rgba(211, 47, 47, 0.05) 100%)',
                borderRadius: '12px',
                border: '2px solid rgba(244, 67, 54, 0.3)'
              }}>
                <span style={{ fontSize: '32px' }}>⚠️</span>
                <div>
                  <h3 style={{ margin: 0, color: '#C62828', fontSize: '1.4rem', fontWeight: 700 }}>
                    Allergy Warning
                  </h3>
                  <p style={{ margin: '4px 0 0 0', color: '#D32F2F', fontSize: '0.9rem' }}>
                    This meal contains ingredients you're allergic to
                  </p>
                </div>
              </div>

              {/* Meal Info */}
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ margin: '0 0 8px 0', color: '#3A3055', fontSize: '1.1rem' }}>
                  {allergyWarningData.meal.name}
                </h4>
                {allergyWarningData.meal.description && (
                  <p style={{ margin: 0, color: '#5A4A6A', fontSize: '0.9rem', lineHeight: 1.5 }}>
                    {allergyWarningData.meal.description}
                  </p>
                )}
              </div>

              {/* Allergens Found */}
              <div style={{
                padding: '16px',
                background: 'rgba(244, 67, 54, 0.08)',
                borderRadius: '12px',
                marginBottom: '24px',
                border: '1px solid rgba(244, 67, 54, 0.2)'
              }}>
                <h4 style={{ margin: '0 0 12px 0', color: '#C62828', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>🚫</span>
                  <span>Contains Your Allergens:</span>
                </h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {allergyWarningData.allergens.map((allergen, idx) => (
                    <span
                      key={idx}
                      style={{
                        padding: '8px 14px',
                        background: '#F44336',
                        color: 'white',
                        borderRadius: '20px',
                        fontSize: '0.85rem',
                        fontWeight: 600
                      }}
                    >
                      {allergen}
                    </span>
                  ))}
                </div>
              </div>

              {/* Why we show this warning + you can still add with caution */}
              <div style={{
                padding: '16px',
                background: 'rgba(255, 193, 7, 0.1)',
                borderRadius: '12px',
                marginBottom: '24px',
                border: '1px solid rgba(255, 193, 7, 0.3)'
              }}>
                <h4 style={{ margin: '0 0 12px 0', color: '#F57C00', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>⚠️</span>
                  <span>Why we flag this:</span>
                </h4>
                <ul style={{ margin: 0, paddingLeft: '20px', color: '#5A4A6A', fontSize: '0.9rem', lineHeight: 1.8 }}>
                  <li>This meal contains ingredients you listed as allergens</li>
                  <li>Consuming allergens can cause severe allergic reactions</li>
                  <li>Even trace amounts can trigger reactions</li>
                </ul>
                <p style={{ margin: '12px 0 0 0', fontSize: '0.85rem', color: '#616161', fontStyle: 'italic' }}>
                  You can still add this meal with your own caution (e.g. if you'll modify the recipe or are sure it's safe for you).
                </p>
              </div>

              {/* Alternative Options */}
              {allergyWarningData.alternatives && allergyWarningData.alternatives.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                  <h4 style={{ margin: '0 0 16px 0', color: '#3A3055', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>✅</span>
                    <span>Safe Alternative Options:</span>
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {allergyWarningData.alternatives.map((altMeal, idx) => (
                      <motion.div
                        key={idx}
                        onClick={() => {
                          // Replace with safe alternative
                          handleMealSelect(allergyWarningData.mealType, altMeal);
                          setShowAllergyWarningModal(false);
                          setAllergyWarningData(null);
                        }}
                        whileHover={{ scale: 1.02, x: 4 }}
                        style={{
                          padding: '14px',
                          background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(56, 142, 60, 0.05) 100%)',
                          borderRadius: '10px',
                          border: '2px solid rgba(76, 175, 80, 0.3)',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <strong style={{ color: '#2E7D32', fontSize: '0.95rem' }}>
                              {altMeal.name}
                            </strong>
                            {altMeal.description && (
                              <p style={{ margin: '4px 0 0 0', color: '#5A4A6A', fontSize: '0.85rem' }}>
                                {altMeal.description.substring(0, 60)}...
                              </p>
                            )}
                          </div>
                          <span style={{ fontSize: '20px' }}>→</span>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                <motion.button
                  onClick={() => {
                    setShowAllergyWarningModal(false);
                    setAllergyWarningData(null);
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    padding: '12px 24px',
                    background: '#E0E0E0',
                    color: '#424242',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </motion.button>
                <motion.button
                  onClick={() => {
                    const meal = { ...allergyWarningData.meal, hasAllergens: true, allergens: allergyWarningData.allergens };
                    if (meal.omega3 || meal['omega-3']) {
                      let v = meal.omega3 || meal['omega-3'] || 0;
                      if (v > 0 && v < 1) v = v * 1000;
                      meal.omega3 = v;
                      delete meal['omega-3'];
                    }
                    setMeals(prev => ({ ...prev, [allergyWarningData.mealType]: meal }));
                    setShowAllergyWarningModal(false);
                    setAllergyWarningData(null);
                    setShowRecommendations(prev => ({ ...prev, [allergyWarningData.mealType]: false }));
                    setSelectedMealInfo({ meal, mealType: allergyWarningData.mealType });
                    setShowMealInfoModal(true);
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    padding: '12px 24px',
                    background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(255, 152, 0, 0.3)'
                  }}
                >
                  Add anyway (I'll be cautious)
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}

      {/* Nutrient-Rich Meals Modal */}
      {showNutrientMealsModal && selectedNutrient && createPortal(
        <AnimatePresence>
          <motion.div
            className="nutrient-meals-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => {
              setShowNutrientMealsModal(false);
              setSelectedNutrient(null);
              setNutrientRichMeals([]);
            }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 99999,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              padding: '20px',
              paddingTop: `${headerHeight}px`,
              overflowY: 'auto'
            }}
          >
            <motion.div
              className="nutrient-meals-modal"
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'white',
                borderRadius: '20px',
                padding: '30px',
                maxWidth: '900px',
                width: '100%',
                maxHeight: `calc(100vh - ${headerHeight + 40}px)`,
                overflowY: 'auto',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
                marginTop: '20px'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <div>
                  <h3 style={{ margin: 0, color: '#3A3055', fontSize: '1.75rem', fontWeight: 700 }}>
                    🍽️ {(() => {
                      const normalized = selectedNutrient.toLowerCase().replace(/-/g, '');
                      if (normalized === 'omega3' || normalized === 'omega-3') return 'Omega-3-Rich Meals';
                      return selectedNutrient.charAt(0).toUpperCase() + selectedNutrient.slice(1) + '-Rich Meals';
                    })()}
                  </h3>
                  <p style={{ margin: '8px 0 0 0', color: '#6B5B73', fontSize: '0.95rem' }}>
                    Choose a meal to add to your daily plan and boost your {(() => {
                      const normalized = selectedNutrient.toLowerCase().replace(/-/g, '');
                      if (normalized === 'omega3' || normalized === 'omega-3') return 'Omega-3';
                      return selectedNutrient.charAt(0).toUpperCase() + selectedNutrient.slice(1);
                    })()} intake
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowNutrientMealsModal(false);
                    setSelectedNutrient(null);
                    setNutrientRichMeals([]);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: '#999',
                    padding: '0',
                    width: '30px',
                    height: '30px'
                  }}
                >
                  ✕
                </button>
              </div>

              {loadingNutrientMeals ? (
                <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    style={{ fontSize: '3rem', marginBottom: '20px' }}
                  >
                    ⏳
                  </motion.div>
                  <p style={{ color: '#6B5B73', fontSize: '1rem' }}>
                    Finding the best {selectedNutrient}-rich meals for you...
                  </p>
                </div>
              ) : nutrientRichMeals.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                  <div style={{ fontSize: '4rem', marginBottom: '20px' }}>🔍</div>
                  <p style={{ color: '#6B5B73', fontSize: '1rem', marginBottom: '12px' }}>
                    No {selectedNutrient}-rich meals found at the moment.
                  </p>
                  <p style={{ color: '#9E9E9E', fontSize: '0.9rem' }}>
                    Try adding meals from the meal boards above to boost your {selectedNutrient} intake!
                  </p>
                </div>
              ) : (
                <>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                    gap: '20px',
                    marginBottom: '24px'
                  }}>
                    {nutrientRichMeals.map((meal, idx) => {
                      // Normalize nutrient key for lookup
                      let normalizedKey = selectedNutrient.toLowerCase().replace(/-/g, '');
                      if (normalizedKey === 'omega3' || normalizedKey === 'omega-3') {
                        normalizedKey = 'omega3';
                      }
                      
                      // Get nutrient value - check multiple possible field names
                      let nutrientValue = meal[normalizedKey] || meal[selectedNutrient.toLowerCase()] || meal[selectedNutrient.toLowerCase().replace(/-/g, '')] || 0;
                      
                      // Special handling for omega-3: also check nutrients array and tags
                      if ((normalizedKey === 'omega3' || normalizedKey === 'omega-3') && !nutrientValue) {
                        const mealText = (meal.name || '').toLowerCase() + ' ' + (meal.description || '').toLowerCase() + ' ' + (meal.ingredients || []).join(' ').toLowerCase();
                        const omegaFoods = ['salmon', 'tuna', 'sardines', 'walnut', 'walnuts', 'chia', 'flax', 'flaxseed', 'cod', 'mackerel', 'herring', 'anchovy'];
                        const hasOmegaFoods = omegaFoods.some(food => mealText.includes(food));
                        const hasOmegaInNutrients = meal.nutrients?.some(n => n.toLowerCase().includes('omega'));
                        const hasOmegaInTags = meal.tags?.some(t => typeof t === 'string' && t.toLowerCase().includes('omega'));
                        
                        if (hasOmegaFoods || hasOmegaInNutrients || hasOmegaInTags) {
                          nutrientValue = 50; // Default estimate (mg) for omega-3 rich foods
                        }
                      }
                      
                      // Normalize omega-3 units: convert grams to milligrams if needed
                      if ((normalizedKey === 'omega3' || normalizedKey === 'omega-3') && nutrientValue > 0) {
                        // If value is less than 1, it's likely in grams (e.g., 0.2g = 200mg)
                        // If value is 1 or greater, assume it's already in mg
                        if (nutrientValue < 1) {
                          nutrientValue = nutrientValue * 1000; // Convert grams to milligrams
                        }
                      }
                      
                      const goal = nutrientGoals?.[normalizedKey] || nutrientGoals?.[selectedNutrient.toLowerCase()] || 0;
                      const percentageOfGoal = goal > 0 ? ((nutrientValue / goal) * 100).toFixed(0) : 0;
                      const unit = selectedNutrient === 'protein' || selectedNutrient === 'fiber' ? 'g' : 
                                  selectedNutrient === 'iron' || selectedNutrient === 'calcium' ? 'mg' : 
                                  (selectedNutrient === 'omega3' || selectedNutrient === 'omega-3') ? 'mg' : 'mcg';
                      
                      // Format nutrient name for display
                      const getNutrientDisplayName = (nut) => {
                        const normalized = nut.toLowerCase().replace(/-/g, '');
                        if (normalized === 'omega3' || normalized === 'omega-3') return 'Omega-3';
                        return nut.charAt(0).toUpperCase() + nut.slice(1);
                      };
                      
                      return (
                        <motion.div
                          key={idx}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: idx * 0.05 }}
                          style={{
                            background: 'white',
                            borderRadius: '16px',
                            padding: '20px',
                            border: '2px solid rgba(233, 30, 99, 0.2)',
                            boxShadow: '0 4px 12px rgba(233, 30, 99, 0.1)',
                            transition: 'all 0.3s ease',
                            cursor: 'pointer'
                          }}
                          whileHover={{ 
                            scale: 1.02, 
                            y: -4,
                            borderColor: 'rgba(233, 30, 99, 0.4)',
                            boxShadow: '0 8px 24px rgba(233, 30, 99, 0.2)'
                          }}
                        >
                          <div style={{ marginBottom: '16px' }}>
                            <h4 style={{ 
                              margin: '0 0 8px 0', 
                              color: '#3A3055', 
                              fontSize: '1.1rem',
                              fontWeight: 700
                            }}>
                              {meal.name}
                            </h4>
                            {meal.description && (
                              <p style={{ 
                                margin: 0, 
                                color: '#6B5B73', 
                                fontSize: '0.85rem',
                                lineHeight: 1.5,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden'
                              }}>
                                {meal.description}
                              </p>
                            )}
                          </div>
                          
                          <div style={{
                            padding: '12px',
                            background: 'linear-gradient(135deg, rgba(233, 30, 99, 0.1) 0%, rgba(236, 64, 122, 0.05) 100%)',
                            borderRadius: '10px',
                            marginBottom: '16px',
                            border: '1px solid rgba(233, 30, 99, 0.2)'
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ color: '#3A3055', fontSize: '0.9rem', fontWeight: 600 }}>
                                {getNutrientDisplayName(selectedNutrient)}:
                              </span>
                              <span style={{ color: '#E91E63', fontSize: '1rem', fontWeight: 700 }}>
                                {nutrientValue.toFixed(1)} {unit}
                              </span>
                            </div>
                            <div style={{ 
                              marginTop: '6px',
                              fontSize: '0.8rem',
                              color: '#6B5B73'
                            }}>
                              ~{percentageOfGoal}% of daily goal
                            </div>
                          </div>

                          {meal.prepTime && (
                            <div style={{ 
                              marginBottom: '16px',
                              fontSize: '0.85rem',
                              color: '#9E9E9E',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}>
                              ⏱️ {meal.prepTime}
                            </div>
                          )}

                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            {['breakfast', 'lunch', 'dinner', 'snack'].map((mealType) => (
                              <motion.button
                                key={mealType}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Normalize omega-3 value before adding meal
                                  const normalizedMeal = { ...meal };
                                  if (normalizedMeal.omega3 || normalizedMeal['omega-3']) {
                                    let omega3Value = normalizedMeal.omega3 || normalizedMeal['omega-3'] || 0;
                                    // Convert grams to milligrams if needed
                                    if (omega3Value > 0 && omega3Value < 1) {
                                      omega3Value = omega3Value * 1000;
                                    }
                                    // Ensure omega3 is set (not omega-3) for consistency
                                    normalizedMeal.omega3 = omega3Value;
                                    delete normalizedMeal['omega-3'];
                                  }
                                  handleMealSelect(mealType, normalizedMeal);
                                  setShowNutrientMealsModal(false);
                                  setSelectedNutrient(null);
                                  setNutrientRichMeals([]);
                                }}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                style={{
                                  flex: 1,
                                  minWidth: '80px',
                                  padding: '8px 12px',
                                  background: meals[mealType] 
                                    ? 'rgba(233, 30, 99, 0.1)' 
                                    : 'linear-gradient(135deg, #E91E63, #EC407A)',
                                  color: meals[mealType] ? '#E91E63' : 'white',
                                  border: `2px solid ${meals[mealType] ? 'rgba(233, 30, 99, 0.3)' : 'transparent'}`,
                                  borderRadius: '8px',
                                  fontSize: '0.8rem',
                                  fontWeight: 600,
                                  cursor: meals[mealType] ? 'not-allowed' : 'pointer',
                                  opacity: meals[mealType] ? 0.6 : 1,
                                  fontFamily: 'var(--font-body)'
                                }}
                                disabled={!!meals[mealType]}
                                title={meals[mealType] ? `You already have ${mealType} planned. Remove it first to replace.` : `Add to ${mealType}`}
                              >
                                {mealType === 'breakfast' && '🌅'}
                                {mealType === 'lunch' && '☀️'}
                                {mealType === 'dinner' && '🌙'}
                                {mealType === 'snack' && '🍎'}
                                {' '}
                                {mealType.charAt(0).toUpperCase() + mealType.slice(1)}
                              </motion.button>
                            ))}
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                  
                  <div style={{ 
                    padding: '16px',
                    background: 'linear-gradient(135deg, rgba(233, 30, 99, 0.08) 0%, rgba(236, 64, 122, 0.05) 100%)',
                    borderRadius: '12px',
                    border: '1px solid rgba(233, 30, 99, 0.2)',
                    marginBottom: '20px'
                  }}>
                    <p style={{ margin: 0, color: '#6B5B73', fontSize: '0.9rem', lineHeight: 1.6 }}>
                      💡 <strong>Tip:</strong> Select any meal above and choose which meal time (breakfast, lunch, dinner, or snack) you'd like to add it to. 
                      Meals marked with a meal type icon already have that slot filled - remove the existing meal first to replace it.
                    </p>
                  </div>
                </>
              )}

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <motion.button
                  onClick={() => {
                    setShowNutrientMealsModal(false);
                    setSelectedNutrient(null);
                    setNutrientRichMeals([]);
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    padding: '12px 24px',
                    background: '#E0E0E0',
                    color: '#424242',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Close
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
}

export default DailyMealBuilder;

